import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { apiRequest } from "@/lib/api";
import { api } from "@/lib/api-typed";
import { APP_LOGO } from "@/lib/branding";
import type { ResponseOf } from "@/types/api";
import { useMutationWithToast } from "@/hooks/shared/use-mutation-with-toast";

interface Notification {
  id: string;
  user_id: string;
  title: string;
  message: string;
  notification_type: string;
  is_read: boolean;
  reference_id: string | null;
  reference_type: string | null;
  // Payload livre — usado para deep-link extra (ex.: { comment_id }).
  data: Record<string, unknown> | null;
  created_at: string;
  /** `info` | `warning` | `critical` — alertas de negócio e antigas (sempre `info`). */
  severity: string;
  /** Do alerta, ou deduzida no servidor para as antigas. */
  category: string | null;
  /** Preenchido quando a condição de um alerta acabou. */
  resolved_at: string | null;
}

export interface NotificationFilters {
  limit?: number;
  category?: string;
  severity?: string;
  unreadOnly?: boolean;
  includeResolved?: boolean;
  /** `priority`: por ler primeiro e, dentro delas, o mais grave. */
  sort?: "recent" | "priority";
}



/** Querystring dos filtros — só o que está definido, para a chave de cache e o pedido coincidirem. */
function notificationQuery(filters: NotificationFilters): string {
  const q = new URLSearchParams({ limit: String(filters.limit ?? 50) });
  if (filters.category) q.set("category", filters.category);
  if (filters.severity) q.set("severity", filters.severity);
  if (filters.unreadOnly) q.set("unread_only", "true");
  if (filters.includeResolved) q.set("include_resolved", "true");
  if (filters.sort) q.set("sort", filters.sort);
  return q.toString();
}

export const useNotifications = (limitOrFilters: number | NotificationFilters = 50) => {
  const filters: NotificationFilters = typeof limitOrFilters === "number" ? { limit: limitOrFilters } : limitOrFilters;
  return useQuery({
    queryKey: ["notifications", "list", notificationQuery(filters)],
    queryFn: async () => {
      return apiRequest<ResponseOf<"/notifications", "get">>(`/notifications?${notificationQuery(filters)}`);
    },
    // A lista tem de acompanhar o badge (useUnreadCount também faz poll a 30s).
    // Sem isto, o badge avançava mas a lista ficava em cache (staleTime 60s) →
    // badge "6" mas popover "Sem notificações" até dar refresh à página.
    refetchInterval: 30_000,
    staleTime: 0,
  });
};

const NOTIFICATION_ICONS: Record<string, string> = {
  training_assigned: "🎓",
  training_new_version: "🎓",
  vacation_requested: "🌴",
  vacation_status: "🌴",
  sick_leave_created: "🏥",
  sick_leave_status: "🏥",
  kb_comment: "📖",
  kb_new_version: "📖",
};

function showBrowserNotification(title: string, body: string, type: string) {
  if (!("Notification" in window) || Notification.permission !== "granted") return;
  try {
    const icon = NOTIFICATION_ICONS[type] ?? "🔔";
    // `undefined` e não `""`: sem logótipo, o browser usa o ícone dele;
    // um URL vazio dava uma notificação com imagem partida.
    new Notification(`${icon} ${title}`, { body, icon: APP_LOGO || undefined, requireInteraction: false });
  } catch {
    // Uma notificação do sistema que falhe não pode partir o que o utilizador
    // está a fazer — o aviso já aparece na app.
  }
}

export const useUnreadCount = () => {
  return useQuery({
    queryKey: ["notifications", "unread-count"],
    queryFn: async () => {
      return api.get("/notifications/unread-count");
    },
    refetchInterval: 30_000,
  });
};

/** Contagens por categoria e severidade (só as abertas) — alimentam os filtros e a cor do selo do sino. */
export const useNotificationSummary = () => {
  return useQuery({
    queryKey: ["notifications", "summary"],
    queryFn: () => api.get("/notifications/summary"),
    refetchInterval: 30_000,
  });
};

export const useBatchNotifications = () => {
  return useMutationWithToast({
    mutationFn: (body: { ids: string[]; action: "read" | "unread" | "delete" }) =>
      api.post("/notifications/batch", { body }),
    invalidateQueries: [["notifications"]],
  });
};

export const useCategoryPreferences = () => {
  return useQuery({
    queryKey: ["notification-category-preferences"],
    queryFn: () => api.get("/notifications/category-preferences"),
    staleTime: 30_000,
  });
};

export const useUpdateCategoryPreferences = () => {
  const queryClient = useQueryClient();
  return useMutationWithToast({
    mutationFn: (muted: string[]) =>
      api.put("/notifications/category-preferences", { body: { muted } }),
    onSuccessCallback: (data) => {
      queryClient.setQueryData(["notification-category-preferences"], data);
    },
  });
};

export const useNotificationBrowserAlerts = () => {
  const seenIdsRef = useRef<Set<string>>(new Set());
  const initializedRef = useRef(false);

  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission().catch(() => {});
    }
  }, []);

  // Reage aos dados da própria query (que já faz poll a 30s via useNotifications)
  // em vez de um setInterval independente — antes eram 3 timers de 30s a correr
  // em paralelo (lista + unread-count + este). Agora o alerta dispara quando a
  // lista é actualizada, sem polling extra.
  const { data } = useNotifications();

  useEffect(() => {
    if (!("Notification" in window) || Notification.permission !== "granted") return;
    if (!data) return;

    if (!initializedRef.current) {
      data.filter((n) => !n.is_read).forEach((n) => seenIdsRef.current.add(n.id));
      initializedRef.current = true;
      return;
    }

    for (const n of data) {
      if (!n.is_read && !seenIdsRef.current.has(n.id)) {
        seenIdsRef.current.add(n.id);
        showBrowserNotification(n.title, n.message, n.notification_type);
      }
    }
  }, [data]);
};


export const useMarkAllRead = () => {
  return useMutationWithToast({
    mutationFn: async () => {
      return api.patch("/notifications/read-all");
    },
    invalidateQueries: [["notifications"]],
  });
};



export type { Notification };
